import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../../data/brand";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.4 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const LINES = ["BUILD", "MOMENTUM.", "MASTER", "MOVEMENT."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-end"
    >
      {/* Parallax image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        initial={{ scale: 1.25, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.hero}
          alt="Coaching in focus"
          className="w-full h-[120%] object-cover object-center grayscale-[30%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs tracking-[0.3em] uppercase font-bold text-[#d4af37] mb-6"
        >
          15-Day Momentum Boot Camp
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter text-5xl sm:text-7xl lg:text-[8rem]">
          {LINES.map((line, i) => (
            <span key={line} className="reveal-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i % 2 === 1 ? "gold-text pl-8 sm:pl-24" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:justify-between"
        >
          <p className="max-w-md text-base sm:text-lg font-light text-neutral-300 leading-relaxed">
            For every fitness level. Learn the foundations of movement and
            strength, build momentum, and get closer to your goal — over 15
            coached sessions.
          </p>

          <a
            href="#apply"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-[#d4af37] text-black px-8 py-4 font-bold text-sm tracking-[0.1em] uppercase hover:bg-[#f3e5ab] transition-colors duration-300 shrink-0"
          >
            Secure Your Spot — $600 + Tax
            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
